import {
  ArrayStringFilterData,
  ArrayStringFilterOperator,
} from "@redotech/redo-model/views/advanced-filters/array-string-filter";
import {
  BooleanFilterData,
  BooleanFilterOperator,
} from "@redotech/redo-model/views/advanced-filters/boolean-filter";
import {
  DateFilterData,
  DateFilterOperator,
  KnownDateFilterTimeFrame,
} from "@redotech/redo-model/views/advanced-filters/date-filter";
import {
  ExistsFilterData,
  ExistsFilterOperator,
} from "@redotech/redo-model/views/advanced-filters/exists-filter";
import {
  AdvancedFilterType,
  decodeFilterGroup,
  encodeFilterGroup,
  GenericAdvancedFilterData,
  isFilterGroup,
} from "@redotech/redo-model/views/advanced-filters/generic-advanced-filter-data";
import {
  NumberFilterData,
  NumberFilterOperator,
  NumberValue,
} from "@redotech/redo-model/views/advanced-filters/number-filter";
import {
  StringFilterData,
  StringFilterOperator,
} from "@redotech/redo-model/views/advanced-filters/string-filter";
import { getFilterBuilder } from "@redotech/redo-model/views/utils/util";
import {
  AdvancedFilterData,
  FilterGroup,
} from "@redotech/redo-model/views/views";
import { deepCopyObject } from "@redotech/util/object";
import { assertNever } from "@redotech/util/type";
import { JSXElementConstructor } from "react";
import { RedoListItem } from "../arbiter-components/list/redo-list";

export interface GenericAdvancedTableFilter<VALUE, OPERATOR extends string> {
  type: AdvancedFilterType;
  data: GenericAdvancedFilterData<VALUE, OPERATOR>;
  operators?: OPERATOR[];
  Icon: JSXElementConstructor<any>;
  openOnRender?: boolean;
  displayName: string;
}

export interface ArrayStringTableFilter<T = string>
  extends GenericAdvancedTableFilter<string[], ArrayStringFilterOperator> {
  data: ArrayStringFilterData;
  type: AdvancedFilterType.ARRAY_TO_STRING;
  itemLabel: string;
  itemLabelPlural?: string;
  valueFetcher?: (
    {
      filters,
      filterSearch,
      applyTableState,
    }: {
      filters: AdvancedTableFilter[];
      filterSearch?: string;
      applyTableState?: boolean;
    },
    signal: AbortSignal,
  ) => Promise<T[]>;
  itemGenerator?: (item: T) => RedoListItem<T>;
  stringify?: (item: T) => string;
  display?: (item: T) => string;
  values?: T[];
  dbSearch?: boolean;
  applyTableState?: boolean;
  useSingleSelect?: boolean;
}

export interface DateTableFilter
  extends GenericAdvancedTableFilter<
    KnownDateFilterTimeFrame,
    DateFilterOperator
  > {
  type: AdvancedFilterType.DATE;
  data: DateFilterData;
  values?: KnownDateFilterTimeFrame[];
}

export interface NumberTableFilter
  extends GenericAdvancedTableFilter<NumberValue, NumberFilterOperator> {
  type: AdvancedFilterType.NUMBER;
  data: NumberFilterData;
  min?: number;
  max?: number;
  step?: number;
  precision?: number;
  prefix?: string;
  suffix?: string;
  modifier?: number;
  secondField?: {
    scalar?: number;
    min?: number;
    max?: number;
    step?: number;
    prefix?: string;
    suffix?: string;
  };
  values?: number[];
}

/**
 * @deprecated Use ArrayStringTableFilter instead.
 * This interface is maintained only for database migration purposes.
 * All new filter implementations should use ArrayStringTableFilter.
 */
export interface StringTableFilter<T = string>
  extends GenericAdvancedTableFilter<string, StringFilterOperator> {
  type: AdvancedFilterType.STRING;
  data: GenericAdvancedFilterData<string, StringFilterOperator>;
  valueFetcher?: (
    { filters }: { filters: AdvancedTableFilter[] },
    signal: AbortSignal,
  ) => Promise<T[]>;
  itemGenerator?: (item: T) => RedoListItem<T>;
  stringify?: (item: T) => string; // value string
  display?: (value: T) => string; // display string
  values?: T[];
}

export interface ExistsTableFilter<T = boolean>
  extends GenericAdvancedTableFilter<boolean, ExistsFilterOperator> {
  type: AdvancedFilterType.EXISTS;
  data: GenericAdvancedFilterData<boolean, ExistsFilterOperator>;
  boolDisplay?: { falseDisplay: string; trueDisplay: string };
  label?: string;
}

export interface BooleanTableFilter
  extends GenericAdvancedTableFilter<boolean, BooleanFilterOperator> {
  type: AdvancedFilterType.BOOLEAN;
  data: GenericAdvancedFilterData<boolean, BooleanFilterOperator>;
  boolDisplay?: { falseDisplay: string; trueDisplay: string };
  label?: string;
}

export type AdvancedTableFilter =
  | ArrayStringTableFilter<any>
  | DateTableFilter
  | NumberTableFilter
  | StringTableFilter<any>
  | ExistsTableFilter
  | BooleanTableFilter;

export function encodeAdvancedFilters(filters: AdvancedTableFilter[]): string {
  let query = "";
  let first = true;
  for (const filter of filters) {
    const filterBuilder = getFilterBuilder(filter.data);
    if (!first) {
      query += ";;";
    }
    query += filter.data.name;
    query += "@@";
    query += filterBuilder.writeToString(filter.data);
    first = false;
  }
  return query;
}

export function encodeAdvancedFiltersData(
  filterData: AdvancedFilterData[],
): string {
  let query = "";
  let first = true;
  for (const filter of filterData) {
    const filterBuilder = getFilterBuilder(filter);
    if (!first) {
      query += ";;";
    }
    query += filter.name;
    query += "@@";
    query += filterBuilder.writeToString(filter);
    first = false;
  }
  return query;
}

export function decodeAdvancedFilters(
  encoded: string,
  advancedFilters: AdvancedTableFilter[],
): AdvancedTableFilter[] {
  if (!encoded) {
    return [];
  }
  const filtersStrings = encoded.split(";;");
  return filtersStrings
    .map((filterString) => {
      const [name, dataString] = filterString.split("@@");
      const filter = advancedFilters.find((f) => f.data.name === name);
      if (!filter) {
        return null;
      }
      const deepClone = deepCopyObject(filter);
      const filterBuilder = getFilterBuilder(deepClone.data);
      return {
        ...deepClone,
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        data: filterBuilder.readFromString(name, dataString, deepClone.data),
      };
    })
    .filter((f) => f !== null) as AdvancedTableFilter[];
}

// Encode a FilterGroup to URL string format
// Format: (and:filter1@@val;;filter2@@val) or nested (and:(or:a@@1;;b@@2);;c@@3)
export function encodeAdvancedFilterGroup(group: FilterGroup): string {
  return encodeFilterGroup(group, (filter) => {
    const filterBuilder = getFilterBuilder(filter);
    return `${filter.name}@@${filterBuilder.writeToString(filter)}`;
  });
}

// Decode a URL string to FilterGroup
// Returns null if parsing fails
export function decodeAdvancedFilterGroup(
  encoded: string,
  advancedFilters: AdvancedTableFilter[],
): FilterGroup | null {
  if (!encoded) {
    return null;
  }

  return decodeFilterGroup<AdvancedFilterData>(encoded, (filterStr) => {
    const [name, dataString] = filterStr.split("@@");
    if (!name || dataString === undefined) {
      return null;
    }

    const filter = advancedFilters.find((f) => f.data.name === name);
    if (!filter) {
      return null;
    }

    const deepClone = deepCopyObject(filter);
    const filterBuilder = getFilterBuilder(deepClone.data);
    const data = filterBuilder.readFromString(name, dataString, deepClone.data);
    return data ?? null;
  });
}

// Convert a FilterGroup to a flat array of AdvancedTableFilter (for display purposes)
// Only works for simple AND groups at the top level
export function filterGroupToTableFilters(
  group: FilterGroup,
  advancedFilters: AdvancedTableFilter[],
): AdvancedTableFilter[] {
  const result: AdvancedTableFilter[] = [];

  for (const clause of group.clauses) {
    if (isFilterGroup(clause)) {
      // Recursively flatten nested groups (loses structure info)
      result.push(...filterGroupToTableFilters(clause, advancedFilters));
    } else {
      const filter = advancedFilters.find((f) => f.data.name === clause.name);
      if (filter) {
        const filterCopy = deepCopyObject(filter);
        filterCopy.data = clause;
        result.push(filterCopy);
      }
    }
  }

  return result;
}

export function dataToDummyTableFilter(
  data: AdvancedFilterData,
): AdvancedTableFilter {
  const type: AdvancedFilterType = data.type;
  switch (type) {
    case AdvancedFilterType.ARRAY_TO_STRING: {
      const tableFilter: ArrayStringTableFilter = {
        type: AdvancedFilterType.ARRAY_TO_STRING,
        Icon: () => null,
        itemLabel: "",
        data: data as ArrayStringFilterData,
        displayName: "",
      };
      return tableFilter;
    }
    case AdvancedFilterType.DATE: {
      const tableFilter: DateTableFilter = {
        type: AdvancedFilterType.DATE,
        Icon: () => null,
        data: data as DateFilterData,
        values: [],
        displayName: "",
      };
      return tableFilter;
    }
    case AdvancedFilterType.NUMBER: {
      const tableFilter: NumberTableFilter = {
        type: AdvancedFilterType.NUMBER,
        Icon: () => null,
        data: data as NumberFilterData,
        displayName: "",
      };
      return tableFilter;
    }
    case AdvancedFilterType.STRING: {
      const tableFilter: StringTableFilter = {
        type: AdvancedFilterType.STRING,
        Icon: () => null,
        data: data as StringFilterData,
        displayName: "",
      };
      return tableFilter;
    }
    case AdvancedFilterType.EXISTS: {
      const tableFilter: ExistsTableFilter = {
        type: AdvancedFilterType.EXISTS,
        Icon: () => null,
        data: data as ExistsFilterData,
        displayName: "",
      };
      return tableFilter;
    }
    case AdvancedFilterType.BOOLEAN: {
      const tableFilter: BooleanTableFilter = {
        type: AdvancedFilterType.BOOLEAN,
        Icon: () => null,
        data: data as BooleanFilterData,
        displayName: "",
      };
      return tableFilter;
    }
    default:
      assertNever(type);
  }
}
